import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import MainVideoCard from '../Card/MainVideoCard';
import MediaLibraryCard from '../Card/MediaLibraryCard';
import { getVideoCourses, getVideos } from '@/services/strapi/strapiCategoryService';
import Loader from '../Loader/Loader';
import type { Video, VideoCourse, VideoCourseResponse, VideoResponse } from '@/types/video';
import { ROUTES } from '@/config/routes';
import { T } from '@tolgee/react';
import type { Language } from '@/types/language';
import { useSelectedCategories } from '@/redux/selectors/categorySelector';
import { useSelectedLanguage } from '@/redux/selectors/languageSelector';

const MediaLibrarySection: React.FC = () => {
  const [videoCourses, setVideoCourses] = useState<VideoCourseResponse[] | null>(null);
  const [videos, setVideos] = useState<VideoResponse[] | null>(null);
  const [loading, setLoading] = useState(true);
  const locale = useSelectedLanguage();
  const selectedCategories = useSelectedCategories();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const categorySlugs = selectedCategories.filter((category) => category !== 'all');

        const videoCoursesResponse = await getVideoCourses(4, categorySlugs, 0, locale);
        setVideoCourses(videoCoursesResponse.data);

        const videoResponse = await getVideos(4, categorySlugs, 0, locale);
        setVideos(videoResponse.data);

        setLoading(false);
      } catch (_error) {
        console.error('Error fetching media items');
        setLoading(false);
      }
    };

    void fetchContent();
  }, [locale, selectedCategories]);

  if (loading) {
    return <Loader position={'absolute'} />;
  }

  if (!videos || videos.length === 0) {
    return <p>No videos found.</p>;
  }

  if (!videoCourses || videoCourses.length === 0) {
    return <p>No video courses found.</p>;
  }

  const mainVideo = {
    imageSrc: videos[0].attributes.thumbnail?.data?.attributes.url,
    imageAlt: videos[0].attributes.title,
    title: videos[0].attributes.title,
    author: videos[0].attributes.expert.data?.attributes.name,
    id: videos[0].id,
    languages: videos[0].attributes.languages.data
      ?.map((language: Language) => language.attributes.name)
      ?.join(', '),
    videos: videos.map((video) => ({
      src: video.attributes.thumbnail?.data?.attributes.url,
      title: video.attributes.title,
      author: video.attributes.expert.data?.attributes.name,
      duration: '',
      type: '',
      id: video.id,
      videoUrl: video.attributes.videoUrl,
      languages: video.attributes.languages.data
        .map((language: Language) => language.attributes.name)
        .join(', '),
    })) as Video[],
  };

  const otherVideos = videoCourses.slice(1).map((videoCourse) => ({
    imageSrc: videoCourse.attributes.thumbnail.data?.attributes.url,
    imageAlt: videoCourse.attributes.title,
    title: videoCourse.attributes.title,
    name: videoCourse.attributes.expert.data?.attributes.name,
    id: videoCourse.id,
    videos: videoCourse.attributes.videos.data.map((video) => ({
      imageAlt: video.attributes.title,
      imageSrc: video.attributes.thumbnail?.data?.attributes.url,
      title: video.attributes.title,
      duration: '',
      author: videoCourse.attributes.expert.data?.attributes.name,
      id: video.id,
      courseId: videoCourse.id,
    })) as VideoCourse[],
  }));

  return (
    <div className="relative p-5 md:p-12 bg-gray">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-4xl md:text-6xl px-5 md:px-0 leading-tight w-full md:w-1/2 m-auto py-4 break-words">
          <T>home.library_section.title.label</T>
        </h2>
        <Link href="/library" className="text-blue-500 text-sm" passHref prefetch={true}>
          See full library
        </Link>
      </div>

      {/* Main Video Card */}
      <Link href={ROUTES.VIDEO(mainVideo.id, mainVideo.title)} passHref prefetch={true}>
        <MainVideoCard {...mainVideo} />
      </Link>

      {/* Media Library Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5 md:mt-4">
        {otherVideos.map((video, index) => (
          <Link
            key={index}
            href={ROUTES.VIDEO_COURSE(video.id, video.title)}
            passHref
            prefetch={true}
          >
            <MediaLibraryCard key={index} {...video} />
          </Link>
        ))}
      </div>
    </div>
  );
};

export default MediaLibrarySection;
